/**
 * Account Service
 * 
 * Handles all bank account-related API calls.
 * 
 * @validates Requirements 4.1 - Account management
 */

import api from './api';
import { BankAccount, CreateBankAccountRequest, UpdateBankAccountRequest, ApiResponse } from '@budgira/shared';

export interface AccountBalance {
  accountId: string;
  balance: number;
  reconciledBalance: number;
  projectedBalance: number;
  calculatedAt: string;
}

export interface BalanceHistoryEntry {
  date: string;
  balance: number;
  /** Balance based on pointed operations only (actual/confirmed) */
  actualBalance?: number;
  /** Balance based on all operations (including non-pointed/projected) */
  projectedBalance?: number;
  /** Whether this date is in the past (true) or future (false) */
  isPast: boolean;
}

export const accountService = {
  /**
   * Get all accounts for the current user
   */
  async list(): Promise<ApiResponse<BankAccount[]>> {
    const response = await api.get<ApiResponse<BankAccount[]>>('/accounts');
    return response.data;
  },

  /**
   * Get a specific account
   */
  async getById(id: string): Promise<ApiResponse<BankAccount>> {
    const response = await api.get<ApiResponse<BankAccount>>(`/accounts/${id}`);
    return response.data;
  },

  /**
   * Create a new account
   */
  async create(data: CreateBankAccountRequest): Promise<ApiResponse<BankAccount>> {
    const response = await api.post<ApiResponse<BankAccount>>('/accounts', data);
    return response.data;
  },

  /**
   * Update an account
   */
  async update(id: string, data: UpdateBankAccountRequest): Promise<ApiResponse<BankAccount>> {
    const response = await api.put<ApiResponse<BankAccount>>(`/accounts/${id}`, data);
    return response.data;
  },

  /**
   * Delete an account
   */
  async delete(id: string): Promise<ApiResponse<null>> {
    const response = await api.delete<ApiResponse<null>>(`/accounts/${id}`);
    return response.data;
  },

  /**
   * Get account balance
   */
  async getBalance(id: string): Promise<ApiResponse<AccountBalance>> {
    const response = await api.get<ApiResponse<AccountBalance>>(`/accounts/${id}/balance`);
    return response.data;
  },

  /**
   * Get account balance history for a specific month
   * @param id - Account ID
   * @param year - Year (optional, defaults to current year)
   * @param month - Month 1-12 (optional, defaults to current month)
   */
  async getBalanceHistory(
    id: string,
    year?: number,
    month?: number
  ): Promise<ApiResponse<BalanceHistoryEntry[]>> {
    const params = new URLSearchParams();
    if (year !== undefined) params.append('year', year.toString());
    if (month !== undefined) params.append('month', month.toString());
    
    const queryString = params.toString();
    const url = `/accounts/${id}/balance-history${queryString ? `?${queryString}` : ''}`;
    
    const response = await api.get<ApiResponse<BalanceHistoryEntry[]>>(url);
    return response.data;
  },
};

export default accountService;
